// Amos-83 Request Portal — every requester-facing string lives here.
// CLASS A: this file IS the copy packet for Shelle + Dorella (CP2 lists it verbatim).
// Keep it plain. No jargon from the queue (no "triage", "class B", "nil", "render").

export const COPY = {
  // ── Sign-in ──
  appTitle: 'Loss Run Request Portal',
  appSubtitle: 'TransEleven Claims Managers',
  signInEmail: 'Email',
  signInPassword: 'Password',
  signInButton: 'Sign in',
  signInBusy: 'Signing in…',
  signInInvite: 'Access is by invitation. If you need an account, contact lossrunrequest@trans11claims.com.',
  signInFailed: 'We could not sign you in. Check your email and password and try again.',
  // ── Amos-104 R1 auth screens — PROVISIONAL, for Shelle's review ──
  signInForgot: 'Forgot your password?',
  signInBackToSignIn: 'Back to sign in',
  signInNewTitle: 'Set a new password',
  signInNewIntro: 'You need to choose a new password before you continue.',
  signInNewLabel: 'New password',
  signInNewConfirmLabel: 'Confirm new password',
  signInNewButton: 'Set password',
  signInNewBusy: 'Saving…',
  signInPasswordRules: 'At least 12 characters, with an upper-case letter, a lower-case letter and a number.',
  signInPasswordMismatch: 'The two passwords do not match.',
  signInPasswordWeak: 'That password does not meet the rules.',
  signInNewExpired: 'Your sign-in has timed out. Please sign in again.',
  signInForgotTitle: 'Reset your password',
  signInForgotIntro: 'Enter your email. If it matches an account, we will email you a code.',
  signInForgotButton: 'Send code',
  signInForgotBusy: 'Sending…',
  signInResetTitle: 'Enter your code',
  signInResetSent: 'If that email matches an account, a code is on its way. It can take a few minutes.',
  signInResetCodeLabel: 'Code from the email',
  signInResetButton: 'Reset password',
  signInResetBusy: 'Resetting…',
  signInResetResend: 'Send a new code',
  signInResetBadCode: 'That code is not valid or has expired. Check it, or send a new code.',
  signInResetLimit: 'Too many tries. Please wait a while, then try again.',
  signInResetDone: 'Your password has been changed. Please sign in.',
  signInResetFailed: 'We could not reset your password. Please try again.',
  // ── Amos-104 S1 (chunk 2026-11-02-S): inline password rules + change password — PROVISIONAL, for Shelle's review ──
  signInRuleLength: 'At least 12 characters',
  signInRuleUpper: 'An upper-case letter',
  signInRuleLower: 'A lower-case letter',
  signInRuleNumber: 'A number',
  accountChangePassword: 'Change password',
  accountChangeTitle: 'Change your password',
  accountCurrentLabel: 'Current password',
  accountChangeButton: 'Change password',
  accountChangeBusy: 'Saving…',
  accountChangeCancel: 'Cancel',
  accountChangeDone: 'Your password has been changed.',
  accountWrongCurrent: 'Your current password is not correct.',
  accountSamePassword: 'Choose a password different from your current one.',
  // Amos-104 R5/H2 (verbatim from Richard 2026-10-21)
  signInNotice: 'This portal is for authorized users of TransEleven Claims Managers. Activity on this portal is recorded. If you were not issued an account, do not attempt to sign in.',
  sessionExpired: 'Your session has ended. Please sign in again.',

  // ── Every screen ── Amos-104 R5 footer + H3 tab title (verbatim). No Terms link until a Terms document exists.
  footer: '© 2026 TransEleven Claims Managers · Loss runs and policy information are confidential and provided only to the parties named on the request. · Questions: lossrunrequest@trans11claims.com',
  footerEmail: 'lossrunrequest@trans11claims.com',
  tabTitle: 'Loss Run Request Portal — TransEleven Claims Managers',
  loading: 'Loading…',

  // ── Shell ──
  signOut: 'Sign out',
  tabNew: 'New request',
  tabMine: 'My requests',
  brokerLabel: (group: string) => `${group.charAt(0).toUpperCase()}${group.slice(1)} policies`,
  testBanner: 'Test account — requests you submit are tagged as test and will not be sent.',
  notEnabled: 'Your account is not set up for a broker group yet. Contact lossrunrequest@trans11claims.com.',

  // ── Form: entry ──
  formHeading: 'Request a loss run',
  formIntro: 'Enter the policy number(s) you need loss runs for. We check each one against your policy list before anything is submitted.',
  modeSingle: 'One policy',
  modePaste: 'Paste a list',
  modeUpload: 'Upload a spreadsheet',
  singleLabel: 'Policy number',
  singlePlaceholder: 'e.g. MPL000581',
  pasteLabel: 'Policy numbers',
  pastePlaceholder: 'Paste any text that contains policy numbers — one per line, comma-separated, or an email body. We pick out the numbers.',
  uploadLabel: 'Spreadsheet (.xlsx, .xlsm or .csv)',
  uploadHint: 'We read every sheet and pick out the policy numbers. PDF is not supported.',
  // Amos-104 G3: label + help line verbatim (the address is the group backstop: lossrunrequest.us@convexin.com)
  deliverToLabel: 'Also send to',
  deliverToPlaceholder: 'name@company.com — separate several with commas',
  deliverToHint: (backstop: string) => `The loss run goes to you and anyone you add. ${backstop} is always copied.`,
  noteLabel: 'Note for the claims team (optional)',
  notePlaceholder: 'Anything we should know — renewal date, specific periods, who asked.',
  checkButton: 'Check policies',
  checking: 'Checking policies…',

  // ── Form: review ──
  reviewHeading: 'Review before submitting',
  reviewFound: (n: number) => (n === 1 ? '1 policy found' : `${n} policies found`),
  reviewFoundIntro: 'These policies are on your list. Each one becomes a separate loss run request.',
  // Amos-104 G2: "Choose periods" removed for the pilot — every request covers all periods.
  // Amos-104 repeat warning (final wording goes to Shelle in the G1 note)
  repeatWarning: (date: string) => `You requested this policy on ${date}; submit anyway?`,
  repeatConfirm: 'Yes, submit anyway',
  repeatPending: 'Confirm each policy you requested before, or go back and remove it.',
  reviewNotFound: (n: number) => (n === 1 ? '1 policy number not found' : `${n} policy numbers not found`),
  reviewNotFoundIntro: 'These are not on the policy list we hold for you. They will not be submitted. Check the number, or email lossrunrequest@trans11claims.com if you believe the policy should be there.',
  reviewBadShape: (n: number) => (n === 1 ? '1 entry is not a policy number' : `${n} entries are not policy numbers`),
  reviewBadShapeIntro: 'Policy numbers look like three letters followed by six digits, for example MPL000581.',
  reviewNothing: 'None of the entries matched a policy on your list, so there is nothing to submit.',
  submitButton: (n: number) => (n === 1 ? 'Submit 1 request' : `Submit ${n} requests`),
  submitting: 'Submitting…',
  backButton: 'Back',

  // ── Form: done ──
  doneHeading: 'Request submitted',
  doneIntro: (n: number) => (n === 1 ? 'Your request is with the claims team.' : `Your ${n} requests are with the claims team.`),
  doneWhatNext: 'The team reviews each request, prepares the loss run and emails it to the addresses on the request. You can follow progress under My requests.',
  doneNotFoundReminder: (n: number) => (n === 1 ? '1 policy number was not found and was not submitted.' : `${n} policy numbers were not found and were not submitted.`),
  // Amos-104: Not-found is shown here once; it is not kept as a row under My requests
  doneNotFoundIntro: 'These are not on the policy list we hold for you. Check the number, or email lossrunrequest@trans11claims.com if you believe the policy should be there.',
  doneFailed: (n: number) => `${n} request(s) could not be saved. Please try those again or contact lossrunrequest@trans11claims.com.`,
  doneAgain: 'Submit another request',
  doneViewMine: 'View my requests',
  doneReplayed: 'This request had already been submitted — nothing was sent twice.',

  // ── Validation / errors ──
  errNoRefs: 'We could not find any policy numbers. Enter a number like MPL000581.',
  errTooMany: (max: number) => `That is more than ${max} policy numbers. Please split the list.`,
  errBadFile: 'That file type is not supported. Upload .xlsx, .xlsm or .csv.',
  errBadEmail: (addr: string) => `"${addr}" is not a valid email address.`,
  errDeliverToMissingAt: 'Each address needs an @ and a domain, for example name@company.com.',
  errGeneric: 'Something went wrong on our side. Please try again in a moment.',
  errNetwork: 'We could not reach the request service. Check your connection and try again.',

  // ── Status table ──
  statusHeading: 'My requests',
  statusIntro: 'Everything you have submitted, newest first.',
  colPolicy: 'Policy',
  colInsured: 'Insured',
  colSubmitted: 'Submitted',
  colStatus: 'Status',
  colUpdated: 'Updated',
  statusReceived: 'Received',
  statusInReview: 'In review',
  statusSent: 'Sent',
  statusClosed: 'Closed',
  statusNotFound: 'Not found',
  // Amos-104 §d (approved): "Sent <date> to <To> — accepted by our mail server"
  statusSentDetail: (date: string, to: string) => `Sent ${date} to ${to} — accepted by our mail server`,
  statusHelp: {
    received: 'We have your request and it is waiting for the claims team.',
    in_review: 'The claims team is working on it.',
    sent: 'The loss run has been emailed to the addresses on the request.',
    closed: 'The team closed this request without sending a new loss run — usually because one was sent recently or the request was a duplicate. Email lossrunrequest@trans11claims.com if you need it.',
    not_found: 'This policy number was not on your policy list, so no request was created.',
  } as Record<string, string>,
  statusEmpty: 'No requests yet.',
  statusEmptyHint: 'Use New request to submit your first one.',
  statusLoading: 'Loading your requests…',
  statusError: 'We could not load your requests right now.',
  statusRefresh: 'Refresh',
  statusLoadMore: 'Load more',
  statusReasonBadShape: 'not a policy number',
  statusReasonNotInList: 'not on your policy list',
};

export const STATUS_LABEL: Record<string, string> = {
  received: COPY.statusReceived,
  in_review: COPY.statusInReview,
  sent: COPY.statusSent,
  closed: COPY.statusClosed,
  not_found: COPY.statusNotFound,
};

/** Flat list for the CLASS A packet: every string with its screen location. */
export function copyPacket(): Array<{ key: string; where: string; text: string }> {
  const where = (k: string) =>
    k.startsWith('signIn') || k === 'appTitle' || k === 'appSubtitle' || k === 'sessionExpired' ? 'Sign-in'
      : k.startsWith('form') || k.startsWith('mode') || k.startsWith('single') || k.startsWith('paste') || k.startsWith('upload') || k.startsWith('deliverTo') || k.startsWith('note') || k === 'checkButton' || k === 'checking' ? 'New request — entry'
      : k.startsWith('review') || k.startsWith('repeat') || k === 'submitButton' || k === 'submitting' || k === 'backButton' ? 'New request — review'
      : k.startsWith('done') ? 'New request — submitted'
      : k.startsWith('err') ? 'Validation messages'
      : k.startsWith('status') || k.startsWith('col') ? 'My requests'
      : k.startsWith('footer') || k === 'tabTitle' || k === 'loading' ? 'Every screen'
      : k.startsWith('account') ? 'Change password'
      : 'Shell';
  const out: Array<{ key: string; where: string; text: string }> = [];
  for (const [k, v] of Object.entries(COPY)) {
    if (typeof v === 'string') out.push({ key: k, where: where(k), text: v });
    else if (typeof v === 'function') {
      const sample = k === 'brokerLabel' ? (v as any)('convex') : k === 'errTooMany' ? (v as any)(600) : k === 'errBadEmail' ? (v as any)('name@company') : k === 'deliverToHint' ? (v as any)('lossrunrequest.us@convexin.com')
        : k === 'repeatWarning' ? (v as any)('Oct 21, 2026') : k === 'statusSentDetail' ? (v as any)('Oct 21, 2026', 'name@company.com') : (v as any)(2);
      const why = k === 'deliverToHint' ? '(address comes from the broker group)' : k === 'brokerLabel' ? '(group name)'
        : k === 'repeatWarning' ? '(date of your last request for the policy)' : k === 'statusSentDetail' ? '(send date and To addresses)' : '(varies with count)';
      out.push({ key: k, where: where(k), text: `${sample}  ${why}` });
    } else if (typeof v === 'object') {
      for (const [kk, vv] of Object.entries(v as Record<string, string>)) out.push({ key: `${k}.${kk}`, where: 'My requests — status help', text: vv });
    }
  }
  return out;
}
